import table from "../copy.json";
import type { Language } from "../types";

type Strings = Record<string, string | string[]>;
const all = table as unknown as Record<string, Strings>;

/** Words for a language, falling back **per key** rather than per language.
 *
 *  The old version fell back only when a whole language was missing, so a key
 *  present in English and absent in Portuguese rendered the literal text
 *  "undefined" on a customer's screen. Every new key shipped that landmine six
 *  times over. English is a poor answer but it is an answer. */
export function words(language: Language) {
  const chosen = all[language] ?? {};
  const english = all.en!;

  return {
    t(key: string, vars?: Record<string, string>): string {
      const raw = chosen[key] ?? english[key] ?? "";
      const value = Array.isArray(raw) ? raw.join(" ") : raw;
      if (!vars) return value;
      const filled = Object.entries(vars).reduce(
        (out, [name, replacement]) => out.split(`{${name}}`).join(replacement),
        value
      );
      return language === "it" && "date" in vars ? elideItalian(filled) : filled;
    },
    list(key: string): string[] {
      const raw = chosen[key] ?? english[key] ?? [];
      return Array.isArray(raw) ? raw : [raw];
    },
  };
}

export type Words = ReturnType<typeof words>;

/** Italian elides the article before a day said with a vowel: "l'8 ottobre",
 *  "dall'11", never "il 8". The copy writes the article because every other
 *  day takes it, so a sentence that carries a date is put right once the date
 *  is in. The same rule as withDates() in functions/src/lib/templates.ts. */
const IT_ELIDED: Record<string, string> = {
  il: "l'", dal: "dall'", al: "all'", del: "dell'", nel: "nell'", sul: "sull'",
};

function elideItalian(text: string): string {
  return text.replace(
    /\b(il|dal|al|del|nel|sul) (1|8|11)(?![0-9]| ?cm)/gi,
    (_, article: string, day: string) => {
      const elided = IT_ELIDED[article.toLowerCase()] ?? `${article} `;
      const cased =
        article[0] === article[0]!.toUpperCase() ? elided[0]!.toUpperCase() + elided.slice(1) : elided;
      return `${cased}${day}`;
    }
  );
}

/** The same locale for each language as the mails use (DATE_LOCALE in
 *  functions/src/lib/templates.ts), so "6 October" is not "October 6" on the
 *  page after the mail said it the other way round. */
const DATE_LOCALE: Record<Language, string> = {
  en: "en-GB",
  pt: "pt-PT",
  nl: "nl-NL",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
  es: "es-ES",
  zh: "zh-TW",
  "zh-hans": "zh-CN",
};

/** Their own language's rendering of a date the mails also quote: the day
 *  their photo is deleted, the day the code's date runs out. In Lisbon time,
 *  as the mails write it, so the two cannot be a day apart near midnight. */
export function formatDate(iso: string, language: Language): string {
  try {
    return new Intl.DateTimeFormat(DATE_LOCALE[language] ?? language, {
      day: "numeric",
      month: "long",
      timeZone: "Europe/Lisbon",
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toISOString().slice(0, 10);
  }
}
